/**
 * Hook for ApplyRuleBasedSendersSection: select, add manual, AI generation.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useState, useCallback } from 'react';
import { useAiSenderGeneration } from '@/features/smswiser-campaigns/components/ScaleDropModal/modals/scale-or-edit/creative/useAiSenderGeneration';
import {
  filterSenderNamesMax11,
  parseSenderNamesInput,
  uniqueKeepOrderStrings,
} from '@/features/smswiser-campaigns/components/PrepareNewDropsForm';

const MAX_SELECTED_SENDERS = 50;

export interface UseApplyRuleBasedSendersParams {
  countryKey: string;
  campaignSenders: string[];
  selectedSenders: string[];
  onSendersChange: (senders: string[]) => void;
  onPerDropBatchesChange: (batches: string[][] | null) => void;
  dropCount?: number;
  isBusy: boolean;
}

export function useApplyRuleBasedSenders(params: UseApplyRuleBasedSendersParams) {
  const {
    countryKey,
    campaignSenders: _campaignSenders,
    selectedSenders,
    onSendersChange,
    onPerDropBatchesChange,
    dropCount: _dropCount,
    isBusy,
  } = params;

  const [showAddNew, setShowAddNew] = useState(false);
  const [newSenderValue, setNewSenderValue] = useState('');
  const [newSenderError, setNewSenderError] = useState<string | null>(null);

  const addSenders = useCallback(
    (senders: string[]) => {
      const valid = filterSenderNamesMax11(senders);
      const merged = filterSenderNamesMax11(
        uniqueKeepOrderStrings([...selectedSenders, ...valid]),
      ).slice(0, MAX_SELECTED_SENDERS);
      onSendersChange(merged);
      setNewSenderValue('');
      setNewSenderError(null);
    },
    [selectedSenders, onSendersChange],
  );

  const applyNewSenderInput = useCallback(() => {
    const { senders, error } = parseSenderNamesInput(newSenderValue);
    if (error) {
      setNewSenderError(error);
      return;
    }
    if (senders.length) addSenders(senders);
  }, [newSenderValue, addSenders]);

  const ai = useAiSenderGeneration({
    countryKey,
    onAddSenders: async () => {},
    onSaveGeneratedAndSelectOnly: (senders) => {
      const merged = filterSenderNamesMax11(
        uniqueKeepOrderStrings([...selectedSenders, ...senders]),
      ).slice(0, MAX_SELECTED_SENDERS);
      onSendersChange(merged);
    },
    onSaveGeneratedPerDrop: async (batches) => {
      onPerDropBatchesChange(batches);
    },
    onPerDropSendersApplied: onPerDropBatchesChange,
    onAiGeneratedDeselectOld: () => onSendersChange([]),
  });

  return {
    ai,
    isBusy,
    showAddNew,
    setShowAddNew,
    newSenderValue,
    setNewSenderValue,
    newSenderError,
    setNewSenderError,
    addSenders,
    applyNewSenderInput,
  };
}
